<template>
  <!-- A finished contract's History tab in the app (lib/view/contracts/detail/: the Preview /
       Overview / History tabs, "Completed", "Audit trail intact", the contractActivity* events
       "Invitation sent to {name}", "{name} verified their email", "{name} viewed the document",
       "{name} signed", "Signed copy sealed", "Signed copies sent to everyone", "Invoice drafted",
       "Automatic", and the Downloads "Signed PDF", "Certificate of completion", "Evidence file").
       Event names are the app's; people and times are sample data. Once in view the events land
       oldest first, then the trail check settles. Resting styles are the final frame. -->
  <div ref="rootEl" class="sch2" :class="{ 'is-run': running || armed, 'is-armed': armed }">
    <div class="sch2-window">
      <header class="sch2-head">
        <span class="sch2-title"><b translate="no">Service agreement</b><small translate="no">Northwind Studio</small></span>
        <span class="sch2-chip sch2-chip--green">Completed</span>
      </header>
      <nav class="sch2-tabs"><span>Preview</span><span>Overview</span><span class="is-on">History</span></nav>
      <p class="sch2-chain" :style="{ '--d': `${0.2 + events.length * 0.32}s` }">
        <svg viewBox="0 0 24 24"><path :d="shield" /></svg>Audit trail intact
      </p>
      <ol class="sch2-feed">
        <li v-for="(event, i) in events" :key="event.title" class="sch2-event" :class="`sch2-event--${event.tone}`" :style="{ '--d': `${0.2 + (events.length - 1 - i) * 0.32}s` }">
          <i class="sch2-dot" />
          <span class="sch2-copy">
            <b translate="no">{{ event.title }}</b>
            <small v-if="event.by">{{ event.by }}</small>
          </span>
          <time translate="no">{{ event.time }}</time>
        </li>
      </ol>
      <div class="sch2-downloads">
        <span v-for="file in downloads" :key="file"><svg viewBox="0 0 24 24"><path :d="download" /></svg>{{ file }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });
// Newest first, as the tab lists them.
const events = [
  { title: "Invoice drafted", by: "Automatic", time: "Thu 4:19 PM", tone: "plain" },
  { title: "Signed copies sent to everyone", by: "Automatic", time: "Thu 4:19 PM", tone: "plain" },
  { title: "Signed copy sealed", by: "Automatic", time: "Thu 4:19 PM", tone: "green" },
  { title: "Jordan Lee signed", time: "Thu 4:18 PM", tone: "plain" },
  { title: "Ana Lopez signed", time: "Thu 3:02 PM", tone: "plain" },
  { title: "Ana Lopez viewed the document", time: "Thu 2:55 PM", tone: "plain" },
  { title: "Ana Lopez verified their email", time: "Thu 2:54 PM", tone: "plain" },
  { title: "Invitation sent to Ana Lopez", time: "Wed 10:30 AM", tone: "plain" },
];
const downloads = ["Signed PDF", "Certificate of completion", "Evidence file"];
const shield = "M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z";
const download = "M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z";
</script>

<style scoped>
.sch2-window {
  max-width: 460px;
  margin: 0 auto;
  border: 1px solid rgb(255 255 255 / 0.13);
  border-radius: 10px;
  background: #1c1c1c;
  padding: 1rem;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.sch2-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.sch2-title { display: grid; min-width: 0; }
.sch2-title b { font-size: 14px; font-weight: 600; }
.sch2-title small { color: #a8a8a8; font-size: 12px; }
.sch2-chip { border-radius: 5px; padding: 2px 8px; font-size: 11.5px; font-weight: 600; }
.sch2-chip--green { background: rgb(63 178 110 / 0.18); color: #6ed39a; }
.sch2-tabs { display: flex; gap: 2px; margin: 12px 0 10px; border-radius: 7px; background: #2a2a2a; padding: 2px; font-size: 11.5px; font-weight: 600; }
.sch2-tabs span { flex: 1; border-radius: 5px; padding: 5px 0; color: #9a9a9a; text-align: center; }
.sch2-tabs .is-on { background: #3a3a3a; color: #ececec; }
.sch2-chain { display: flex; align-items: center; gap: 6px; margin: 0 0 8px; border-radius: 6px; background: rgb(63 178 110 / 0.12); padding: 6px 10px; color: #6ed39a; font-size: 12px; font-weight: 600; }
.sch2-chain svg { width: 14px; height: 14px; fill: currentColor; }
.sch2-feed { display: grid; gap: 2px; margin: 0; padding: 0; list-style: none; }
.sch2-event { display: grid; grid-template-columns: auto 1fr auto; align-items: start; gap: 10px; border-radius: 7px; padding: 5px 8px; }
.sch2-event--green { background: rgb(63 178 110 / 0.12); }
.sch2-dot { width: 8px; height: 8px; margin-top: 6px; border-radius: 3px; background: #6b6b6b; }
.sch2-event--green .sch2-dot { background: #6ed39a; }
.sch2-copy { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0 8px; min-width: 0; }
.sch2-copy b { font-size: 12.5px; font-weight: 500; }
.sch2-copy small { color: #9a9a9a; font-size: 11px; }
.sch2-event time { color: #9a9a9a; font-size: 11px; white-space: nowrap; }
.sch2-downloads { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; border-top: 1px solid rgb(255 255 255 / 0.1); padding-top: 10px; }
.sch2-downloads span { display: inline-flex; align-items: center; gap: 5px; border: 1px solid rgb(255 255 255 / 0.13); border-radius: 6px; padding: 4px 8px; color: #d4d4d4; font-size: 11.5px; }
.sch2-downloads svg { width: 13px; height: 13px; fill: currentColor; }

/* Oldest first: each event lands in its slot (rows never move); the trail check settles last. */
.is-run .sch2-event, .is-run .sch2-chain { animation: sch2-land 0.4s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sch2-land { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: none; } }
@media (max-width: 639px) {
  .sch2-window { padding: 0.8rem; }
  .sch2-event time { display: none; }
}
@media (prefers-reduced-motion: reduce) { .sch2 * { animation: none !important; } }
</style>
